import { useMemo } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useRevenueStore } from '../../store/revenue.store';
import { Download } from 'lucide-react';
import InfoTooltip from './InfoTooltip';
import { format, addDays, addMonths, addWeeks, parse, isBefore, isEqual, startOfISOWeek } from 'date-fns';

type Grp = 'day' | 'week' | 'month';

function formatVND(value: number): string {
  return value.toLocaleString('vi-VN');
}

// Pretty label for x-axis tick
function labelForKey(key: string, groupBy: Grp): string {
  if (groupBy === 'month') {
    // key = "yyyy-MM"
    const [y, m] = key.split('-');
    return `Tháng ${parseInt(m, 10)}/${y}`;
  }
  if (groupBy === 'week') {
    // key = "RRRR-Wnn" (ISO week year - week number)
    const [y, w] = key.split('-W');
    return `Tuần ${parseInt(w, 10)}/${y}`;
  }
  // day: "yyyy-MM-dd" → "dd/MM"
  return format(parse(key, 'yyyy-MM-dd', new Date()), 'dd/MM');
}

function fillGaps(data: { date: string; totalRevenue: number; orderCount: number }[], from: string, to: string, groupBy: Grp) {
  const map = new Map(data.map(d => [d.date, { ...d }]));
  const filled: { date: string; totalRevenue: number; orderCount: number }[] = [];

  if (groupBy === 'day') {
    let cur = parse(from, 'yyyy-MM-dd', new Date());
    const end = parse(to, 'yyyy-MM-dd', new Date());
    while (isBefore(cur, end) || isEqual(cur, end)) {
      const key = format(cur, 'yyyy-MM-dd');
      filled.push(map.get(key) || { date: key, totalRevenue: 0, orderCount: 0 });
      map.delete(key);
      cur = addDays(cur, 1);
    }
  } else if (groupBy === 'week') {
    let cur = startOfISOWeek(parse(from, 'yyyy-MM-dd', new Date()));
    const end = startOfISOWeek(parse(to, 'yyyy-MM-dd', new Date()));
    while (isBefore(cur, end) || isEqual(cur, end)) {
      const key = format(cur, "RRRR-'W'II");
      filled.push(map.get(key) || { date: key, totalRevenue: 0, orderCount: 0 });
      map.delete(key);
      cur = addWeeks(cur, 1);
    }
  } else {
    const fromMonth = from.substring(0, 7);
    const endMonth = to.substring(0, 7);
    let cur = parse(fromMonth + '-01', 'yyyy-MM-dd', new Date());
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const key = format(cur, 'yyyy-MM');
      filled.push(map.get(key) || { date: key, totalRevenue: 0, orderCount: 0 });
      map.delete(key);
      if (key >= endMonth) break;
      cur = addMonths(cur, 1);
    }
  }

  // Merge any remaining out-of-range boundary entries into last bucket
  if (map.size > 0 && filled.length > 0) {
    const lastEntry = filled[filled.length - 1];
    for (const extra of map.values()) {
      lastEntry.totalRevenue += extra.totalRevenue;
      lastEntry.orderCount += extra.orderCount;
    }
  }

  return filled;
}

export default function RevenueTrendChart({ isDark }: { isDark: boolean }) {
  const revenueTrend = useRevenueStore((s) => s.revenueTrend);
  const reportFrom = useRevenueStore((s) => s.reportFrom);
  const reportTo = useRevenueStore((s) => s.reportTo);
  const trendGroupBy = useRevenueStore((s) => s.trendGroupBy);
  const fetchTrend = useRevenueStore((s) => s.fetchTrend);

  const tooltipText = `Biểu đồ doanh thu và số đơn hàng theo thời gian.\n\n• Cột xanh dương: Doanh thu mỗi kỳ (VND)\n• Đường xanh lá: Số đơn mỗi kỳ\n\nChế độ hiển thị:\n• Daily: mỗi cột = 1 ngày\n• Weekly: mỗi cột = 1 tuần (nhãn Tuần n/YYYY, theo ISO week)\n• Monthly: mỗi cột = 1 tháng (nhãn Tháng n/YYYY)\n\nKỳ không có deal vẫn hiển thị với giá trị 0 để biểu đồ liên tục.`;

  const filledData = useMemo(() => {
    if (!revenueTrend.length) return [];
    return fillGaps(revenueTrend, reportFrom, reportTo, trendGroupBy);
  }, [revenueTrend, reportFrom, reportTo, trendGroupBy]);

  const handleGroupByChange = (g: Grp) => {
    if (g === trendGroupBy) return;
    fetchTrend(g);
  };

  const toggleClass = (active: boolean) =>
    `px-2.5 py-1 text-xs font-medium rounded transition-colors ${
      active
        ? 'bg-cyan-500 text-white'
        : isDark
          ? 'text-slate-400 hover:text-white hover:bg-white/10'
          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
    }`;

  const exportCSV = () => {
    const label = trendGroupBy === 'day' ? 'Ngày' : trendGroupBy === 'week' ? 'Tuần' : 'Tháng';
    const csvRows = [`${label},Doanh thu,Số đơn`];
    for (const row of filledData) {
      csvRows.push(`"${labelForKey(row.date, trendGroupBy)}",${row.totalRevenue},${row.orderCount}`);
    }
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_trend_${trendGroupBy}_${reportFrom}_${reportTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!revenueTrend.length) {
    return (
      <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Xu hướng doanh thu
          <InfoTooltip isDark={isDark} text={tooltipText} />
          {' '}
          <span className={`ml-2 text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            ({reportFrom} — {reportTo})
          </span>
        </h3>
        <div className={`flex items-center justify-center h-64 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Chưa có dữ liệu
        </div>
      </div>
    );
  }

  return (
    <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Xu hướng doanh thu
          <InfoTooltip isDark={isDark} text={tooltipText} />
          {' '}
          <span className={`ml-2 text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            ({reportFrom} — {reportTo})
          </span>
        </h3>
        <div className="flex-1" />
        <div className={`inline-flex rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button onClick={() => handleGroupByChange('day')} className={toggleClass(trendGroupBy === 'day')}>Daily</button>
          <button onClick={() => handleGroupByChange('week')} className={toggleClass(trendGroupBy === 'week')}>Weekly</button>
          <button onClick={() => handleGroupByChange('month')} className={toggleClass(trendGroupBy === 'month')}>Monthly</button>
        </div>
        <button onClick={exportCSV} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`}>
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>
      {/* Scorecards — use raw API data so boundary records aren't dropped */}
      {(() => {
        const totalRevenue = revenueTrend.reduce((s, r) => s + r.totalRevenue, 0);
        const totalOrders = revenueTrend.reduce((s, r) => s + r.orderCount, 0);
        const avgPerDay = filledData.length > 0 ? totalRevenue / filledData.length : 0;
        const avgOrderPerDay = filledData.length > 0 ? totalOrders / filledData.length : 0;
        const periodLabel = trendGroupBy === 'day' ? 'ngày' : trendGroupBy === 'week' ? 'tuần' : 'tháng';

        const cards = [
          { label: 'Tổng doanh thu', value: formatVND(totalRevenue) + ' VND', color: 'text-cyan-400' },
          { label: 'Tổng số đơn', value: formatVND(totalOrders), color: 'text-blue-400' },
          { label: `TB / ${periodLabel}`, value: formatVND(Math.round(avgPerDay)) + ' VND', color: 'text-emerald-400' },
          { label: `TB đơn / ${periodLabel}`, value: avgOrderPerDay.toFixed(1), color: 'text-amber-400' },
        ];

        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {cards.map((c) => (
              <div key={c.label} className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{c.label}</p>
                <p className={`text-lg font-bold font-mono mt-1 ${isDark ? c.color : 'text-gray-900'}`}>{c.value}</p>
              </div>
            ))}
          </div>
        );
      })()}

      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={filledData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
          <XAxis
            dataKey="date"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            tickFormatter={(v: string) => labelForKey(v, trendGroupBy)}
            interval={trendGroupBy === 'day' && filledData.length > 14 ? Math.ceil(filledData.length / 14) : 0}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            tickFormatter={formatVND}
            label={{ value: 'Doanh thu', angle: -90, position: 'insideLeft', fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11, offset: 10 }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            label={{ value: 'Số đơn', angle: 90, position: 'insideRight', fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11, offset: 10 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDark ? '#f8fafc' : '#111827',
            }}
            formatter={(value: any, name: any) => [
              name === 'totalRevenue' ? formatVND(value as number) + ' VND' : value,
              name === 'totalRevenue' ? 'Doanh thu' : 'Số đơn',
            ]}
            labelFormatter={(v: string) => labelForKey(v, trendGroupBy)}
            labelStyle={{ color: isDark ? '#f8fafc' : '#111827', fontWeight: 600 }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            formatter={(v: string) => v === 'totalRevenue' ? 'Doanh thu' : 'Số đơn'}
          />
          <Bar yAxisId="left" dataKey="totalRevenue" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={trendGroupBy === 'month' ? 40 : trendGroupBy === 'week' ? 28 : 20} />
          <Line yAxisId="right" type="monotone" dataKey="orderCount" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
